import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { gt } from 'ember-truth-helpers';
import type {
  TablePricingManagerContext,
  TablePricingManagerContextColumn,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import HotelPricingAssistantTableTooltipsTooltipCompetitorCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-competitor-cell';
import type { Rate } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { translateRoomCategory } from 'lumen/utils/pricing-assistant/open-pricing-utils';

import styles from './td-competitors-rate.module.css';

interface HotelPricingAssistantTableCellsTdCompetitorsRateSignature {
  Args: {
    column: TablePricingManagerContextColumn;
    value: Rate;
  };
}

export default class HotelPricingAssistantTableCellsTdCompetitorsRate extends Component<HotelPricingAssistantTableCellsTdCompetitorsRateSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get date(): Dayjs {
    return this.rate.period.fromDate;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.date);
  }

  get rate(): Rate {
    return this.args.value;
  }

  get refDaysAgo(): number {
    return this.tableContext.refDaysAgo;
  }

  get showPriceChangeArrow(): boolean {
    const priceChange = this.rate.priceChangeSinceYesterday;
    return priceChange !== null && priceChange !== 0 && !this.isPast;
  }

  get tableContext(): TablePricingManagerContext {
    return this.args.column.tableContext;
  }

  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      const dateStr = this.date.format('YYYY-MM-DD');
      const hotelName = this.rate.hotelinfo.name;
      const cemaCategory = this.rate.cemaCategory
        ? translateRoomCategory(this.intl, this.rate.cemaCategory)
        : 'N/A';

      console.group(
        `Debug: Pricing Manager Competitor Cell - ${hotelName} - ${cemaCategory} - ${dateStr}`,
      );

      if (console.table && this.rate) {
        console.log('Rate object:');
        console.table(this.rate.toJSON());
      } else {
        console.log('No console.table available or rate is undefined');
      }

      console.groupEnd();
    }
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="text-200--regular flex justify-end gap-100 truncate tabular-nums
        {{if this.isPast 'text-disabled'}}
        "
      data-test-id="td-competitors-rate-cell"
      {{on "dblclick" this.onDoubleClick}}
    >
      {{this.rate.formattedRate}}
      <div class={{styles.cell-icon-block}}>
        {{#if this.showPriceChangeArrow}}
          <PrismIcon
            @color={{if
              (gt this.rate.priceChangeSinceYesterday 0)
              "on-success"
              "on-critical"
            }}
            @name={{if
              (gt this.rate.priceChangeSinceYesterday 0)
              "arrow-up"
              "arrow-down"
            }}
            @size="extra-small"
            data-test-id="competitor-price-change-arrow"
          />
        {{/if}}
      </div>
      <HotelPricingAssistantTableTooltipsTooltipCompetitorCell
        @date={{this.date}}
        @rate={{this.rate}}
        @refDaysAgo={{this.refDaysAgo}}
      />
    </div>
  </template>
}
